import { useEffect, useState, memo } from 'react';
import { Platform, TextProps } from 'react-native';
import { Text, View } from '@constants/Themed';
import React from 'react';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import AnimatedLottieView from '@utils/lottieAnimation';
import { Spinner } from 'tamagui';
interface AnimatedTextProps extends TextProps {
  value: string;
  speed?: number;
  delay?: number;
  fontSize?: number;
  fontColor?: string;
  onAnimationComplete?: Function;
  center?: boolean;
  inverse?: boolean;
  loadingText?: string;
  isLoading?: boolean;
  retain?: number;
}

function AnimatedTextFunction({
  value,
  speed = 200,
  delay,
  fontSize = 12,
  fontColor,
  onAnimationComplete,
  center,
  retain,
  loadingText,
  isLoading,
  inverse,
  ...props
}: AnimatedTextProps) {
  if (!value) return null;
  const words = value.split(' ');
  const [wordIndex, setWordIndex] = useState(0);
  const [opacity, setOpacity] = useState(true);
  const theme = themeSelector();

  useEffect(() => {
    const displayNextWord = () => {
      if (wordIndex < words.length) {
        setWordIndex((index) => index + 1);
      } else {
        setOpacity(false);
        setTimeout(() => onAnimationComplete && onAnimationComplete(), retain || 1000);
      }
    };

    const timeoutId = setTimeout(displayNextWord, speed);

    return () => clearTimeout(timeoutId);
  }, [wordIndex, onAnimationComplete, value]);

  if (!value) {
    return <Text>No value provided for AnimatedTextFunction component.</Text>;
  }

  return (
    <View style={{ flex: 1, flexDirection: inverse ? "row-reverse" : "row", maxWidth: Platform.select({ ios: "100%", android: "100%"}) }}>
      <AnimatedLottieView source={require('@assets/animations/automation_robot.json')} style={{ flex: 1, maxHeight: Platform.select({ ios: "20%", android: "20%" }) }} loop autoPlay />
      <View style={{ flex: 3, backgroundColor: 'white', borderWidth: 3, borderColor: 'rgb(90,192,234)', borderRadius: 20, borderBottomStartRadius: inverse ? undefined : 0, borderBottomEndRadius: inverse ? 0 : undefined, justifyContent: 'center', padding: 10 }}>
        {
          isLoading ?
            <Text style={[{ alignSelf: 'center', alignContent: "center", alignItems: 'center', textAlign: 'center', gap: 20 }]}>
              {loadingText || 'Loading...'}
              <Spinner />
            </Text>
            :
            <Text
              adjustsFontSizeToFit={Platform.OS === 'android' ? false : true}
              style={{
                flex: 1,
                flexWrap: 'wrap',
                fontSize: fontSize,
                fontWeight: '400',
                justifyContent: 'space-evenly',
                textAlign: center ? 'center' : 'justify',
                color: theme && !fontColor ? fontColor || 'black' : fontColor || 'white',
                lineHeight: fontSize * 1.5,
              }}
              {...props}
            >
              {words.slice(0, wordIndex).join(' ')}
              {Platform.OS === 'web' && (
                <Animatable.View useNativeDriver animation="pulse" style={{ opacity: opacity ? 1 : 0, minWidth: 15, flex: 1 }}>
                  <Animatable.Text animation="fadeIn" style={{ color: theme ? 'black' : 'white', flex: 1, lineHeight: fontSize * 1.5 }}>
                    _
                  </Animatable.Text>
                </Animatable.View>
              )}
            </Text>
        }
      </View>
    </View>
  );
}

export default memo(AnimatedTextFunction, (prevProps, nextProps) => {
  // Memoization based on props
  return (
    prevProps.value === nextProps.value &&
    prevProps.speed === nextProps.speed &&
    prevProps.delay === nextProps.delay &&
    prevProps.fontSize === nextProps.fontSize &&
    prevProps.fontColor === nextProps.fontColor &&
    prevProps.center === nextProps.center &&
    prevProps.retain === nextProps.retain
  );
});
